/**
 * WebDashboardSlackViewer - Simplified dashboard viewer for Slack screenshot generation.
 *
 * This component renders a minimal version of the dashboard specifically for
 * generating screenshots to be used in Slack unfurls. It:
 * - Uses token-based authentication (no user login required)
 * - Renders only dashboard blocks by default (no navbar, header, sidebar, etc.)
 * - Limits to 2 rows of blocks for compact screenshots by default
 * - Uses white background for clean screenshots
 */
import { WebDashboardContentSpec } from "@coeff/agent-types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";

import { isAxiosError, useApiContext } from "../../utils";

import { DashboardWindow } from "./dashboardHelpers";
import {
  computeLayoutFromBlocks,
  enforceSectionHeaderIsolation,
  getOrderedBlockIds,
  mergeLayoutWithBlocks,
  snapLayoutFlexWidths,
} from "./layoutHelpers";
import {
  applyTheme,
  DEFAULT_THEME_BACKGROUND,
  DEFAULT_THEME_ID,
  DEFAULT_THEME_MODE,
  isCustomThemeId,
  registerCustomThemeSpec,
  resolveTheme,
  ThemeBackground,
  ThemeMode,
  ThemeProvider,
  ThemeSpec,
} from "./themes";
import {
  FLATPICKR_STYLE_TAG,
  METRIC_STATUS_STYLE_TAG,
  TAILWIND_STYLE_TAG,
} from "./themes/applyTheme";
import { RunSQLFn, useRunSQL } from "./useRunSQL";
import { getScreenshotSQLiteDownloadUrl, getViewAsEmail, useDashboard } from "./webDashboard";
import { WebDashboardContent } from "./WebDashboardContent";
import { WebDashboardViewerContext } from "./WebDashboardViewerContext";

// --------------------------------
// Data Masking for OG Preview (prevents information leakage)
// --------------------------------

/**
 * Mask a single value to prevent information leakage in OG preview images.
 * - Numbers → 0
 * - Strings → "***"
 * - Booleans → false
 * - Everything else → null
 */
function maskValue(value: unknown): unknown {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === "number") {
    return 0;
  }
  if (typeof value === "string") {
    return "***";
  }
  if (typeof value === "boolean") {
    return false;
  }
  if (typeof value === "bigint") {
    return BigInt(0);
  }
  if (Array.isArray(value)) {
    return value.map(maskValue);
  }
  if (typeof value === "object") {
    const masked: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>)) {
      masked[key] = maskValue((value as Record<string, unknown>)[key]);
    }
    return masked;
  }
  return null;
}

/**
 * Mask all values in a query result row.
 */
function maskRow(row: Record<string, unknown>): Record<string, unknown> {
  const masked: Record<string, unknown> = {};
  for (const key of Object.keys(row)) {
    masked[key] = maskValue(row[key]);
  }
  return masked;
}

/**
 * Mask block titles and content in the content spec to prevent information leakage.
 * - metric_card: label → "Metric N"
 * - chart: title → "Chart N"
 * - data_table: title → "Table N"
 * - ai_insights: title → "Insights N", insights → empty array
 * - ai_image: title → "Image N"
 */
function maskContentSpec(spec: WebDashboardContentSpec): WebDashboardContentSpec {
  let metricCount = 0;
  let chartCount = 0;
  let tableCount = 0;
  let insightsCount = 0;
  let imageCount = 0;
  let blockCount = 0;

  const maskedBlocks = spec.blocks.map(block => {
    const masked = { ...block };

    if (block.type === "metric_card") {
      metricCount++;
      (masked as any).label = `Metric ${metricCount}`;
    } else if (block.type === "chart") {
      chartCount++;
      (masked as any).title = `Chart ${chartCount}`;
    } else if (block.type === "data_table") {
      tableCount++;
      (masked as any).title = `Table ${tableCount}`;
    } else if (block.type === "ai_insights") {
      insightsCount++;
      (masked as any).title = `Insights ${insightsCount}`;
      // Clear AI-generated insights content to prevent data leakage
      (masked as any).insights = [];
    } else if (block.type === "ai_image") {
      imageCount++;
      (masked as any).title = `Image ${imageCount}`;
    } else {
      blockCount++;
      (masked as any).title = `Block ${blockCount}`;
    }

    return masked;
  });

  return { ...spec, blocks: maskedBlocks } as WebDashboardContentSpec;
}

const SUBSCRIPTION_BLOCK_IDS_QUERY_PARAM = "subscription_block_ids";

/**
 * Selects the /dashboards list-thumbnail capture preset. Kept in sync with
 * ``LIST_THUMBNAIL_QUERY_PARAM`` in coeff/server/lib/web_dashboard_screenshot_utils.py.
 */
const LIST_THUMBNAIL_QUERY_PARAM = "list_thumbnail";

/**
 * Neutralises the reserved scrollbar gutter for the list-thumbnail capture.
 *
 * The srcDoc sets `scrollbar-gutter: stable` inline on <html>, which permanently reserves the
 * scrollbar's width on the right. That is invisible to an element screenshot, but the list
 * thumbnail is a fixed 1440px-wide clip in PAGE coordinates: the gutter narrows the layout viewport
 * to 1425px, `mx-auto` then centres the content in 1425 instead of 1440, and the capture ends up
 * with ~15px more background on the right than on the left. Zeroing the scrollbar zeroes the gutter
 * with it, so the layout viewport is exactly as wide as the clip and the side padding is symmetric.
 *
 * Scroll position is irrelevant here — the capture grows the viewport to fit the whole band rather
 * than scrolling to it.
 */
const HIDE_SCROLLBAR_STYLE = `
  html { scrollbar-gutter: auto !important; }
  html::-webkit-scrollbar { width: 0 !important; height: 0 !important; }
`;

function parseSubscriptionBlockIds(searchParams: URLSearchParams): string[] {
  const rawBlockIds = searchParams.get(SUBSCRIPTION_BLOCK_IDS_QUERY_PARAM);
  if (!rawBlockIds) {
    return [];
  }

  const seen = new Set<string>();
  return rawBlockIds
    .split(",")
    .map(blockId => blockId.trim())
    .filter(blockId => {
      if (!blockId || seen.has(blockId)) {
        return false;
      }
      seen.add(blockId);
      return true;
    });
}

function getSubscriptionContentSpec(
  spec: WebDashboardContentSpec,
  selectedBlockIds: string[]
): WebDashboardContentSpec {
  if (selectedBlockIds.length === 0) {
    return spec;
  }

  const selectedBlockIdSet = new Set(selectedBlockIds);
  const selectedBlocks = spec.blocks.filter(block => selectedBlockIdSet.has(block.block_id));
  if (selectedBlocks.length === 0) {
    return { ...spec, blocks: [], layout: { rows: [] } };
  }

  const selectedBlockMap = new Map(selectedBlocks.map(block => [block.block_id, block]));
  const initialLayout =
    spec.layout && spec.layout.rows.length > 0
      ? mergeLayoutWithBlocks(spec.layout, selectedBlocks)
      : computeLayoutFromBlocks(selectedBlocks);
  const filteredLayout = snapLayoutFlexWidths(
    enforceSectionHeaderIsolation(initialLayout, selectedBlockMap),
    selectedBlockMap
  );
  const orderedBlocks = getOrderedBlockIds(filteredLayout)
    .map(blockId => selectedBlockMap.get(blockId))
    .filter((block): block is (typeof selectedBlocks)[number] => block !== undefined);

  return {
    ...spec,
    blocks: orderedBlocks,
    layout: filteredLayout,
  };
}

// Simplified iframe content for Slack screenshots - theme-aware background, minimal styles
const slackPreviewIframeSrcDoc = `
<!DOCTYPE html>
<html class="text-sm overscroll-contain bg-page font-themed" style="font-size: 0.875rem; scrollbar-gutter: stable;">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <script src="https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4"></script>
  ${TAILWIND_STYLE_TAG}
  <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet">
  <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Fira+Sans:wght@400;500;600;700&family=Funnel+Display:wght@300;400;500;600;700&family=Inter:wght@400;500;600;700&family=Lexend:wght@400;500;600;700&family=Merriweather:wght@400;700&family=Noticia+Text:wght@400;700&family=Nunito:wght@400;500;600;700&family=Poppins:wght@400;500;600;700&family=Quicksand:wght@400;500;600;700&family=Raleway:wght@400;500;600;700&family=Rubik:wght@400;500;600;700&family=Varela+Round&display=swap" rel="stylesheet">
  <link href="https://unpkg.com/gridjs/dist/theme/mermaid.min.css" rel="stylesheet">
  <link href="https://cdn.jsdelivr.net/npm/flatpickr/dist/flatpickr.min.css" rel="stylesheet">
  ${FLATPICKR_STYLE_TAG}
  ${METRIC_STATUS_STYLE_TAG}
  <script src="https://code.jquery.com/jquery-3.7.1.min.js"
    integrity="sha384-1H217gwSVyLSIfaLxHbE7dRb3v4mYCKbpQvzx0cegeju1MVsGrX5xXxAvs/HgeFs"
    crossorigin="anonymous"></script>
  <!-- Pinned: table sizing reads AG Grid's internal DOM, renamed wholesale in v36 — see the
       matching tag in WebDashboardViewer.tsx (incl. how to recompute integrity on a version
       bump). Keep in sync with webapp/package.json. -->
  <script src="https://cdn.jsdelivr.net/npm/ag-grid-community@34.3.1/dist/ag-grid-community.min.js"
    integrity="sha384-1m5FM32cqWvkVPC2kdiP5dLsQUO3OCeEAGgZ4RqzCWlsWcCVIM9HXAjD1A99GPs3"
    crossorigin="anonymous"></script>
  <script src="https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js"></script>
  <script src="https://cdn.jsdelivr.net/npm/chartjs-plugin-datalabels@2"></script>
  <script src="https://cdn.jsdelivr.net/npm/chartjs-plugin-annotation@3"></script>
  <script src="https://cdn.jsdelivr.net/npm/hammerjs@2.0.8"></script>
  <script src="https://cdn.jsdelivr.net/npm/chartjs-plugin-zoom@2"></script>
  <script src="https://cdn.jsdelivr.net/npm/chartjs-adapter-date-fns@3"></script>
  <script src="https://cdn.jsdelivr.net/npm/chartjs-chart-funnel@4.2.5/build/index.umd.min.js"></script>
  <script src="https://cdn.jsdelivr.net/npm/chartjs-chart-treemap@3.1.0/dist/chartjs-chart-treemap.min.js"></script>
  <script src="https://cdn.jsdelivr.net/npm/chartjs-chart-sankey@0.14.0/dist/chartjs-chart-sankey.min.js"></script>
  <script src="https://cdn.jsdelivr.net/npm/chartjs-chart-matrix@2.0.1/dist/chartjs-chart-matrix.min.js"></script>
  <!-- ChartGeo global; bundles topojson-client (ChartGeo.topojson). Boundary atlases are
       fetched by map blocks at render time from pinned us-atlas@3.0.1 / world-atlas@2.0.2. -->
  <script src="https://cdn.jsdelivr.net/npm/chartjs-chart-geo@4.3.6/build/index.umd.min.js"></script>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/sql.js/1.5.0/sql-wasm.js"></script>
  <script src="https://cdn.jsdelivr.net/npm/flatpickr@4.6"></script>
  <script src="https://cdn.jsdelivr.net/npm/marked@17/lib/marked.umd.js"></script>
  <style>
    .max-w-8xl { max-width: 88rem; }
    .max-w-9xl { max-width: 96rem; }
    .w-8xl { width: 88rem; }
    .w-9xl { width: 96rem; }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    .loading-spinner {
      animation: spin 1s linear infinite;
    }
  </style>
</head>
<body>
  <div id="iframe-root"></div>
</body>
</html>
`;

export const WebDashboardSlackViewer: React.FC = () => {
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const { apiClient } = useApiContext();

  // Get screenshot token and preview mode from query params
  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const screenshotToken = searchParams.get("screenshot_token");
  const isPreviewMode = searchParams.get("preview_mode") === "true";
  const isFullDashboardMode = searchParams.get("full_dashboard") === "true";
  const subscriptionBlockIds = useMemo(
    () => parseSubscriptionBlockIds(searchParams),
    [searchParams]
  );
  const isSubscriptionBlocksMode = subscriptionBlockIds.length > 0;
  const screenshotTargetBlockId =
    subscriptionBlockIds.length === 1 ? subscriptionBlockIds[0] : undefined;
  const useExpandedDashboardLayout = isFullDashboardMode || isSubscriptionBlocksMode;
  /**
   * The /dashboards list-thumbnail capture: all rows, viewer spacing, slicers still rendered.
   *
   * `full_dashboard=true` gets the first two but is unusable here, because it also drives
   * `hideSlicers` — and the capture anchors its top edge to `#slicersContainer`, which then would
   * not exist. This flag is that combination minus the slicer hiding.
   */
  const isListThumbnailMode = searchParams.get(LIST_THUMBNAIL_QUERY_PARAM) === "true";
  /**
   * The list thumbnail is a crop of what a viewer actually sees, so it has to lay out in the real
   * viewer's container — `max-w-9xl mx-auto px-6 my-7`, matching WebDashboardViewer's `<main>`.
   * The compact unfurl padding (`px-4`) leaves the blocks nearly flush with the frame edge, well
   * short of the ~69px inset the design's 1440px-wide reference crop shows (a 1344px `max-w-9xl`
   * container centred in 1440, plus its own 21px `px-6`).
   */
  const useViewerContentSpacing = useExpandedDashboardLayout || isListThumbnailMode;

  const [iframeBody, setIframeBody] = useState<HTMLElement | null>(null);
  const [iframeWindow, setIframeWindow] = useState<DashboardWindow | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    if (iframe?.contentWindow?.document?.body) {
      const root = iframe.contentWindow.document.getElementById("iframe-root");
      if (root) {
        setIframeBody(root);
        setIframeWindow(iframe.contentWindow as DashboardWindow);
      }
    }
  };

  // Fetch dashboard data using screenshot token (no auth required)
  const {
    data: dashboardData,
    isLoading: isLoadingDashboard,
    error: dashboardError,
  } = useDashboard(dashboardId, screenshotToken);

  const hasDashboardSqliteData = Boolean(
    dashboardData?.spreadsheet_version_id || (dashboardData?.tabs?.length ?? 0) > 0
  );

  const {
    runSQL: originalRunSQL,
    isInitialized: isSQLiteInitializedFromHook,
    error: sqliteError,
  } = useRunSQL({
    spreadsheetId: hasDashboardSqliteData ? dashboardData?.spreadsheet_id || null : null,
    spreadsheetVersionId: dashboardData?.spreadsheet_version_id,
    dashboardId: hasDashboardSqliteData ? dashboardId : undefined,
    combinedTablesHash: dashboardData?.combined_tables_hash,
    apiClient,
    isPublicDashboard: dashboardData?.sharing_level === "public",
    sqliteDownloadUrl: getScreenshotSQLiteDownloadUrl(dashboardData),
  });

  useEffect(() => {
    if (!iframeBody) {
      return;
    }
    if (sqliteError || dashboardError) {
      iframeBody.setAttribute("data-dashboard-load-error", "true");
    } else {
      iframeBody.removeAttribute("data-dashboard-load-error");
    }
  }, [dashboardError, iframeBody, sqliteError]);

  // Wrap runSQL with masking for preview mode (OG image generation)
  // This prevents any real data from appearing in public OG preview images
  const runSQL: RunSQLFn = isPreviewMode
    ? async (query: string, bindings?: unknown[]) => {
        const results = await originalRunSQL(query, bindings);
        return results.map(maskRow);
      }
    : originalRunSQL;

  // Get theme from dashboard config. Read-only path: mode/background are plain consts derived
  // from the loaded content_config (no selector, no state).
  const themeId =
    (dashboardData?.content_config as { theme_id?: string } | undefined)?.theme_id ??
    DEFAULT_THEME_ID;
  const mode: ThemeMode =
    (dashboardData?.content_config as { mode?: ThemeMode } | undefined)?.mode ?? DEFAULT_THEME_MODE;
  const background: ThemeBackground =
    (dashboardData?.content_config as { background?: ThemeBackground } | undefined)?.background ??
    DEFAULT_THEME_BACKGROUND;
  // Register the resolved custom theme (embedded in the GET response) so `custom:` ids expand.
  const resolvedTheme = dashboardData?.resolved_theme as ThemeSpec | undefined;
  useMemo(() => {
    if (isCustomThemeId(themeId) && resolvedTheme) {
      registerCustomThemeSpec(themeId, resolvedTheme);
    }
  }, [themeId, resolvedTheme]);

  const contentSpec = useMemo(() => {
    const spec = dashboardData?.content_config?.content_spec as WebDashboardContentSpec | undefined;
    if (!spec) {
      return undefined;
    }

    const subscriptionSpec = getSubscriptionContentSpec(spec, subscriptionBlockIds);
    return isPreviewMode ? maskContentSpec(subscriptionSpec) : subscriptionSpec;
  }, [dashboardData?.content_config?.content_spec, isPreviewMode, subscriptionBlockIds]);

  useEffect(() => {
    if (!iframeWindow) {
      return;
    }

    const blockElements = Array.from(
      iframeWindow.document.querySelectorAll<HTMLElement>("[data-block-id]")
    );
    for (const blockElement of blockElements) {
      if (
        screenshotTargetBlockId &&
        blockElement.getAttribute("data-block-id") === screenshotTargetBlockId
      ) {
        blockElement.setAttribute("data-subscription-screenshot-target", "true");
      } else {
        blockElement.removeAttribute("data-subscription-screenshot-target");
      }
    }
  }, [contentSpec, iframeWindow, screenshotTargetBlockId, isSQLiteInitializedFromHook]);

  // Apply theme when iframe becomes available
  useEffect(() => {
    if (!iframeWindow) {
      return;
    }
    const theme = resolveTheme(themeId, mode, background);
    iframeWindow.__coeffWebDashboardTheme = theme;
    applyTheme(iframeWindow, theme);
    // See WebDashboardViewer: a custom theme edited in place keeps its id, so the resolved spec
    // has to be a dep or the re-registered theme never gets applied.
  }, [themeId, mode, background, iframeWindow, resolvedTheme]);

  // Error states
  const isAccessDenied = isAxiosError(dashboardError) && dashboardError.response?.status === 403;
  const error = !dashboardId
    ? "Dashboard ID is required"
    : !screenshotToken
    ? "Screenshot token is required"
    : isAccessDenied
    ? "Invalid or expired screenshot token"
    : dashboardError
    ? "Failed to load dashboard"
    : null;

  // Show loading iframe while content loads
  if (!iframeBody || !iframeWindow) {
    return (
      <iframe
        ref={iframeRef}
        id="dashboardContentIframe"
        srcDoc={slackPreviewIframeSrcDoc}
        onLoad={handleIframeLoad}
        className="fixed inset-0 w-full h-full border-none z-0"
        style={{ display: "block", border: "none", margin: 0, padding: 0 }}
        frameBorder="0"
        title="Dashboard Frame"
      />
    );
  }

  const content = (
    <div className="min-h-screen bg-page relative">
      {isListThumbnailMode && <style>{HIDE_SCROLLBAR_STYLE}</style>}
      {isLoadingDashboard ? (
        <div className="flex justify-center items-center h-screen">
          <div className="loading-spinner w-10 h-10 rounded-full border-[3px] border-gray-100 border-t-[3px] border-t-blue-500"></div>
        </div>
      ) : error ? (
        <div className="flex justify-center items-center h-screen px-6">
          <div className="bg-red-100 border border-red-500 text-red-900 p-4 rounded-lg">
            <strong>Error:</strong> {error}
          </div>
        </div>
      ) : (
        <div id="dashboardMainContainer" className="w-full bg-dashboard-container">
          {isFullDashboardMode && (
            <header id="mainHeader" className="max-w-9xl mx-auto px-6 my-7">
              <h1
                id="dashboardTitle"
                className="text-[40px] leading-tight font-semibold text-main-foreground mb-1"
              >
                {dashboardData?.content_config?.title || "Dashboard"}
              </h1>
              <p
                id="dashboardDescription"
                className="text-[16px] font-medium text-main-muted-foreground"
              >
                {dashboardData?.content_config?.description || ""}
              </p>
            </header>
          )}

          <main
            id="mainContent"
            className={useViewerContentSpacing ? "max-w-9xl mx-auto px-6 my-7" : "w-full px-4 py-2"}
          >
            {contentSpec ? (
              <WebDashboardContent
                contentSpec={contentSpec}
                runSQL={runSQL}
                targetWindow={iframeWindow}
                initialLayout={contentSpec.layout}
                onLayoutChange={() => {}}
                onBlockDelete={() => {}}
                onExplainBlockInChat={() => {}}
                canEdit={false}
                // Slack unfurls stay compact; subscription captures render all requested rows, and
                // the list thumbnail needs enough rows to fill its fixed-height capture band rather
                // than trailing off into whitespace.
                maxRows={useExpandedDashboardLayout || isListThumbnailMode ? undefined : 2}
                hideSlicers={isSubscriptionBlocksMode || isFullDashboardMode}
                isSQLiteReady={isSQLiteInitializedFromHook}
                dashboardId={dashboardId}
                // A capture has no logged-in viewer, so the identity arrives with the
                // dashboard data (minted into the screenshot token). Without it a
                // personalized_view slicer resolves to "" and every block drops its
                // per-viewer filter, rendering rows this capture must not show.
                userEmail={getViewAsEmail(dashboardData)}
              />
            ) : (
              <p className="text-gray-500">Loading...</p>
            )}
          </main>
        </div>
      )}
    </div>
  );

  return (
    <>
      <iframe
        ref={iframeRef}
        srcDoc={slackPreviewIframeSrcDoc}
        onLoad={handleIframeLoad}
        style={{
          display: "block",
          border: "none",
          margin: 0,
          padding: 0,
          width: "100%",
          height: "100%",
        }}
        frameBorder="0"
        title="Dashboard Frame"
      />
      {createPortal(
        <ThemeProvider initialThemeId={themeId} mode={mode} background={background}>
          <WebDashboardViewerContext.Provider
            value={{
              dashboardMode: "viewer",
              setDashboardMode: () => {},
              dashboardId: dashboardId || "",
              dashboardName: dashboardData?.content_config?.title,
              spreadsheetId: dashboardData?.spreadsheet_id || "",
              isAiThinking: false,
              setIsAiThinking: () => {},
              isArchived: false,
            }}
          >
            {content}
          </WebDashboardViewerContext.Provider>
        </ThemeProvider>,
        iframeBody
      )}
    </>
  );
};
